import type { Capability } from "@/types/Capability";

/**
 * The backend's trash doors (`GuestTrashAccess`), mirrored ONCE for every
 * cockpit surface that offers a trash lane — the Papierkorb page and the
 * dashboard's activity row.
 *
 * Into the trash at all: guests:delete AND an unrestricted role — no row
 * filters, no field allowlist (`GuestTrashAccess.refusalOrNull`, 403
 * `trashScopeRestricted`). Restore writes guests back and needs guests:write
 * on top (`restoreRefusalOrNull`). Mirrored here, like `canReadCohortValues`,
 * so no button is offered that only 403s on the rows/fields rule: the
 * activity row used to gate its restore on the two capabilities alone and
 * hand a scoped CUSTOM role a "Restore guest" whose dry run then refused it
 * (22.10., audit L3 F3).
 *
 * NOT mirrored, because the cockpit never receives it: the restore door's
 * slotting clause (`isSlottingUnrestricted` — the template's offering /
 * treatment / lane allowlists). `src/types/EventRoleDTO.ts` declares
 * `visibleOfferingIds` / `visibleTreatmentIds` / `visibleLaneIds` as
 * optionals, but the wire DTO (`responses/EventRoleDTO.java`) has no such
 * field and the self-fetch never fills them — reading them here would see
 * `undefined` and call every role unrestricted. A role scoped that way still
 * sees the restore and the dry run names the refusal
 * (`trashSlottingScopeRestricted`) inside the dialog. Closing it means the
 * backend projecting those lists (or a `slottingRestricted` flag) onto the
 * self-fetch DTO; until then this is the known gap (audit r3 MED-1, r4 LOW-1).
 *
 * Both fail closed: a capability context that cannot say what the role's
 * scope is counts as a restricted one.
 */
export type GuestTrashCaps = {
  can: (cap: Capability) => boolean;
  rowFilters: readonly unknown[] | undefined;
  fieldAllowlist: readonly string[] | null | undefined;
};

/** The trash door itself — and the early permanent delete behind it. */
export function canUseGuestTrash(caps: GuestTrashCaps): boolean {
  return (
    caps.can("guests:delete") &&
    Array.isArray(caps.rowFilters) &&
    caps.rowFilters.length === 0 &&
    caps.fieldAllowlist === null
  );
}

export const canPurgeFromGuestTrash = canUseGuestTrash;

/** Restore: the trash door plus guests:write. */
export function canRestoreFromGuestTrash(caps: GuestTrashCaps): boolean {
  return canUseGuestTrash(caps) && caps.can("guests:write");
}
